/**
 * IepSectionItemEditDrawer displays a right-sliding drawer form to save, edit,
 * or delete an individual extracted IEP section item (Goal, Service, Accommodation, Modification, PLOP).
 */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import cloneDeep from 'lodash/cloneDeep'
import isBoolean from 'lodash/isBoolean'
import isNumber from 'lodash/isNumber'

import makeStyles from '@mui/styles/makeStyles'
import PropTypes from 'prop-types'

import { Link } from 'react-router-dom'

import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { SnackbarStackContext } from '../../app/contexts'
import { PLAN_TYPE_IPP } from '../../arch/iepConstants'
import { DocumentFileViewContext } from '../../binder/contexts'
import { getHookPromiseErrorHandler } from '../../common'
import { drawerWidth } from '../../commonStyles'
import { patchIepExtractionData } from '../../domain/plan'

import {
  PLAN_DEFAULT,
  PLAN_VARIANTS,
  SECTION_ITEM_LABELS,
  SECTION_KEY_ACCOMMODATIONS,
  SECTION_KEY_GOALS,
  SECTION_KEY_MODIFICATIONS,
  SECTION_KEY_OUTCOMES,
  SECTION_KEY_PLOPS,
  SECTION_KEY_PRIORITIES,
  SECTION_KEY_SERVICES,
  SECTION_KEY_SUPPORTS
} from '../constants'

import { IepWizardContext } from '../contexts'
import ExtractionFieldEditor from '../extraction-editor/ExtractionFieldEditor'
import { fillDefaultKeys, isRequiredFieldsSatisfied, requiredFieldsMessage } from '../extraction-editor/utilities'
import { useExtractionItemEditor } from '../extraction-editor/hooks'

import model from '../../model'

import BrandButton from '../../subcomponents/brand/BrandButton'
import BrandIconButton from '../../subcomponents/brand/BrandIconButton'
import ConfirmationDialog from '../../subcomponents/ConfirmationDialog'
import ErrorSnackbar from '../../subcomponents/ErrorSnackbar'

const RECOMMENDATION_ONLY_FILTERED_VIEW_SECTIONS = [
  SECTION_KEY_GOALS,
  SECTION_KEY_SERVICES,
  SECTION_KEY_ACCOMMODATIONS,
  SECTION_KEY_MODIFICATIONS
]

const useStyles = makeStyles(
  theme => ({
    root: {
      zIndex: theme.zIndex.tooltip
    },

    paper: {
      borderRadius: theme.spacing(1.875, 0, 0, 0),
      minWidth: theme.spacing(drawerWidth(theme)),
      maxWidth: theme.spacing(drawerWidth(theme)),
      width: theme.spacing(drawerWidth(theme)),
      display: 'flex',
      flexDirection: 'column'
    },

    header: {
      display: 'flex',
      gap: theme.spacing(3),
      flexDirection: 'column',
      padding: theme.spacing(3, 3, 0, 4),
      justifyContent: 'space-between',
      alignItems: 'flex-start'
    },

    title: {
      ...theme.typography.h1,
      color: theme.palette.text.primary
    },

    closeIconButton: {
      color: theme.palette.text.primary,
      padding: theme.spacing(0.5),
      marginLeft: theme.spacing(-0.75)
    },

    closeIcon: {
      fontSize: theme.typography.pxToRem(20)
    },

    formContainer: {
      flexGrow: 1,
      overflowY: 'auto',
      padding: theme.spacing(3, 4, 4, 4)
    },

    field: {
      marginBottom: theme.spacing(2.5)
    },

    nestedSection: {
      borderWidth: theme.spacing(0.125),
      borderStyle: 'solid',
      borderColor: theme.palette.divider,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2.5),
      backgroundColor: theme.palette.grey[50]
    },

    nestedHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(1.5)
    },

    nestedTitle: {
      fontWeight: theme.typography.fontWeightBold
    },

    nestedItem: {
      position: 'relative',
      padding: theme.spacing(2),
      borderWidth: theme.spacing(0.125),
      borderStyle: 'solid',
      borderColor: theme.palette.divider,
      borderRadius: theme.spacing(0.75),
      backgroundColor: theme.palette.common.white,
      marginBottom: theme.spacing(1.5)
    },

    nestedDeleteButton: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1)
    },

    jsonBlock: {
      marginTop: theme.spacing(3),
      backgroundColor: theme.palette.border,
      borderWidth: theme.spacing(0.125),
      borderColor: theme.palette.border,
      borderStyle: 'solid',
      borderRadius: theme.spacing(0.75),
      padding: theme.spacing(1, 2)
    },

    jsonTitle: {
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },

    jsonPre: {
      margin: 0,
      fontFamily: 'monospace',
      fontSize: theme.typography.pxToRem(11),
      overflowX: 'auto',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all'
    },

    dangerZone: {
      marginTop: theme.spacing(5),
      display: 'flex',
      justifyContent: 'center'
    },

    deleteButton: {
      borderColor: theme.palette.error.dark,
      color: theme.palette.error.dark,
      padding: theme.spacing(1, 4),
      textTransform: 'none',

      '&:hover': {
        borderColor: theme.palette.error.dark,
        color: theme.palette.error.dark
      }
    },

    footer: {
      borderTopWidth: theme.spacing(0.125),
      borderTopStyle: 'solid',
      borderTopColor: theme.palette.divider,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(2.5, 3),
      backgroundColor: theme.palette.common.white
    },

    footerActions: {
      display: 'flex',
      gap: theme.spacing(2)
    },

    cancelButton: {
      padding: theme.spacing(1, 2)
    },

    saveButton: {
      padding: theme.spacing(1, 3)
    },

    viewIepButton: {
      padding: theme.spacing(1, 2),
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5)
    },

    confirmDialogPaper: {
      borderRadius: theme.spacing(1)
    },

    confirmDialogDeleteButton: {
      borderColor: theme.palette.error.dark,
      color: theme.palette.error.dark,
      padding: theme.spacing(1.5, 4),

      '&:hover': {
        borderColor: theme.palette.error.dark,
        color: theme.palette.error.dark
      }
    },

    confirmDialogCancelButton: {
      padding: theme.spacing(1.5, 4)
    },

    addObjectiveButton: {
      textTransform: 'none'
    },

    requiredFieldsHint: {
      marginBottom: theme.spacing(2),
      color: theme.palette.text.secondary,
      fontSize: theme.typography.pxToRem(13),
      fontStyle: 'italic'
    },

    divider: {
      marginTop: theme.spacing(3),
      marginBottom: 0
    },

    jsonAddIcon: {
      transform: props => (props.jsonOpen ? 'rotate(45deg)' : 'none'),
      transition: 'transform 150ms'
    }
  }),
  {
    name: 'IepSectionItemEditDrawer'
  }
)

const EXTRACTION_SECTION_KEYS = {
  [SECTION_KEY_GOALS]: 'goals',
  [SECTION_KEY_SERVICES]: 'services',
  [SECTION_KEY_ACCOMMODATIONS]: 'accommodationsAndModifications',
  [SECTION_KEY_MODIFICATIONS]: 'accommodationsAndModifications',
  [SECTION_KEY_PLOPS]: 'presentLevels',
  [SECTION_KEY_PRIORITIES]: 'priorities',
  [SECTION_KEY_OUTCOMES]: 'outcomes',
  [SECTION_KEY_SUPPORTS]: 'supports'
}

// Surfaced when a navigator/superuser tries to save or delete before `extractionData` has resolved
// (still loading, or the prefetch failed) — without this, handleSave/handleConfirmDelete would
// silently skip syncing the extraction and still call onSave/onDelete, closing the drawer as if the
// edit had persisted when nothing was actually written back to the extraction document.
const EXTRACTION_UNAVAILABLE_MESSAGE =
  'Extraction data is not available yet. Please close this drawer and try again in a moment.'

const identifiersMatch = (a, b) => a != null && b != null && String(a) === String(b)

// True when `candidate` (an item from the persisted extraction section) is the same item as
// `target` — matched by database id (once persisted) or by uniqueIdentifier UUID (assigned by
// the backend extraction pipeline, since a brand-new item may not have a database id yet).
const isSameExtractionItem = (candidate, target) =>
  identifiersMatch(candidate?.id, target?.id) || identifiersMatch(candidate?.uniqueIdentifier, target?.uniqueIdentifier)

// True when `candidate` carries the id or uniqueIdentifier that `targetIdentifier` refers to.
const extractionItemHasIdentifier = (candidate, targetIdentifier) =>
  identifiersMatch(candidate?.id, targetIdentifier) || identifiersMatch(candidate?.uniqueIdentifier, targetIdentifier)

const getUpdatedSectionItems = (sectionItems, camelItem, sectionKey, insertAfterId, insertAfterIndex) => {
  const itemIndex = sectionItems.findIndex(candidate => isSameExtractionItem(candidate, camelItem))

  if (itemIndex !== -1) {
    const updated = [...sectionItems]
    updated[itemIndex] = camelItem
    return updated
  }

  let finalNewItem = { ...camelItem }

  if ([SECTION_KEY_ACCOMMODATIONS, SECTION_KEY_MODIFICATIONS].includes(sectionKey)) {
    const { type } = camelItem
    const siblingItem = sectionItems.find(x => x.type === type) || sectionItems[0]

    if (siblingItem) {
      const blankItem = {}
      Object.keys(siblingItem).forEach(key => {
        const val = siblingItem[key]
        if (Array.isArray(val)) {
          blankItem[key] = []
        } else if (isBoolean(val)) {
          blankItem[key] = false
        } else {
          blankItem[key] = null
        }
      })
      finalNewItem = {
        ...blankItem,
        ...camelItem
      }
    }
  }

  if (insertAfterId) {
    const insertIndex = sectionItems.findIndex(candidate => extractionItemHasIdentifier(candidate, insertAfterId))

    if (insertIndex !== -1) {
      const updated = [...sectionItems]
      updated.splice(insertIndex + 1, 0, finalNewItem)
      return updated
    }

    // The sibling's id doesn't resolve within this section's extraction payload (it may use a
    // different id scheme than the reviewed `iep[sectionKey]` list the sibling was picked from).
    // Fall back to the sibling's position in that list so the new item still lands where the user
    // clicked "add" rather than always appending at the end.
    if (isNumber(insertAfterIndex)) {
      const updated = [...sectionItems]
      updated.splice(Math.min(insertAfterIndex + 1, updated.length), 0, finalNewItem)
      return updated
    }
  }

  return [...sectionItems, finalNewItem]
}

const IepSectionItemEditDrawer = props => {
  const {
    open,
    item,
    sectionKey,
    documentId,
    planType,
    extractionPatcher = patchIepExtractionData,
    onClose,
    onSave,
    onDelete,
    withRecommendationsOnly
  } = props

  const [jsonOpen, setJsonOpen] = useState(false)
  const [localItem, setLocalItem] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState()

  const handleApiError = useMemo(() => getHookPromiseErrorHandler({ setError }), [])

  const isIppPlan = planType === PLAN_TYPE_IPP
  const { planLabel } = PLAN_VARIANTS[planType] ?? PLAN_DEFAULT

  const { documentFileViewPath } = useContext(DocumentFileViewContext)
  const { isNavigatorOrSuperuser, extractionData, setExtractionData } = useContext(IepWizardContext)
  const { openSnackbar } = useContext(SnackbarStackContext)

  const { extraction } = extractionData ?? {}

  const {
    setExpandedIds,
    handleChange: handleFieldChange,
    handleAddItem,
    handleRemoveItem,
    isExpanded,
    toggleExpanded
  } = useExtractionItemEditor(setLocalItem)

  const handleError = error => {
    const { message, response } = error
    if (response) {
      handleApiError(error)
    } else {
      setError(message)
    }
  }

  const handleObjectiveDateChange = useCallback(
    index => event => {
      handleFieldChange(`secondaryGoals[${index}].targetDate`, event.target.value)
    },
    [handleFieldChange]
  )

  const handleObjectiveTextChange = useCallback(
    index => event => {
      handleFieldChange(`secondaryGoals[${index}].goalText`, event.target.value)
    },
    [handleFieldChange]
  )

  const handleAddObjective = event => {
    event.preventDefault()
    event.stopPropagation()
    setLocalItem(current => {
      if (!current) {
        return current
      }
      const secondaryGoalsList = current.secondaryGoals || []
      const newObjective = { uniqueIdentifier: '', targetDate: '', goalText: '' }
      return {
        ...current,
        secondaryGoals: [...secondaryGoalsList, newObjective]
      }
    })
  }

  const handleRemoveObjective = index => event => {
    event.preventDefault()
    event.stopPropagation()
    setLocalItem(current => {
      if (!current) {
        return current
      }
      const secondaryGoalsList = current.secondaryGoals || []
      return {
        ...current,
        secondaryGoals: secondaryGoalsList.filter((_, itemIndex) => itemIndex !== index)
      }
    })
  }

  const handleSave = async event => {
    event.preventDefault()
    event.stopPropagation()

    if (!onSave || !localItem) {
      return
    }

    setSaving(true)
    try {
      const { insertAfterId, insertAfterIndex, isNew, recommendations } = localItem

      // These are client-only bookkeeping fields (see createBlankItem) that must never leak into
      // persisted data — strip them from the copy sent to the backend/onSave. (Reopening this same
      // item afterward re-syncs from fresh data regardless — see the reopened check in the effect
      // below — so localItem itself is deliberately left untouched here rather than also updated,
      // which would flash the drawer into "edit" mode for an instant before it closes.)
      const camelItem = { ...localItem }
      delete camelItem.insertAfterId
      delete camelItem.insertAfterIndex
      delete camelItem.isNew

      const snakeItem = model.serializeExtractionItem(camelItem)

      // Bail out loudly rather than silently skipping the sync below and letting onSave still
      // close the drawer as if the edit had been written back to the extraction document.
      if (isNavigatorOrSuperuser && !extraction) {
        throw new Error(EXTRACTION_UNAVAILABLE_MESSAGE)
      }

      if (isNavigatorOrSuperuser && extraction) {
        const targetKey = EXTRACTION_SECTION_KEYS[sectionKey] || sectionKey
        const sectionItems = extraction[targetKey] || []

        const updatedSectionItems = getUpdatedSectionItems(
          sectionItems,
          camelItem,
          sectionKey,
          insertAfterId,
          insertAfterIndex
        )

        const nextExtraction = {
          ...extraction,
          [targetKey]: updatedSectionItems
        }

        const snakeExtraction = model.toSnakeCaseKeys(nextExtraction)
        const putPayload = { extraction: snakeExtraction }
        await extractionPatcher(documentId, putPayload)

        setExtractionData({
          ...extractionData,
          extraction: nextExtraction
        })
      }

      await onSave(snakeItem)

      // Fired after onSave closes the drawer (see handleSaveDrawerSettled) so the success
      // message doesn't show while the drawer is still open and about to disappear underneath it.
      if (isNavigatorOrSuperuser && extraction) {
        const defaultTitle = `${sectionLabel} data has been ${isNew ? 'added' : 'updated'} successfully.`

        // Navigators in the withRecommendationsOnly filtered view won't see the newly added item in the UI
        // since it lacks recommendations and they are in the "Only recommendation" filtered view.
        // The snackbar provides feedback explaining why this is.
        const recommendationsOnlyTitle =
          withRecommendationsOnly &&
          RECOMMENDATION_ONLY_FILTERED_VIEW_SECTIONS.includes(sectionKey) &&
          isNew &&
          recommendations?.length === 0
            ? `${sectionLabel} was added but you can't view as you are in "Only recommendation" filtered view`
            : null

        const title = recommendationsOnlyTitle ?? defaultTitle

        openSnackbar({
          message: { title },
          id: `iep-section-update-${sectionKey}-${snakeItem.id || 'new'}`,
          options: { fullTitle: true }
        })
      }
    } catch (error) {
      handleError(error)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = event => {
    event.preventDefault()
    event.stopPropagation()
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async event => {
    setShowDeleteConfirm(false)

    if (onDelete && localItem) {
      setDeleting(true)
      try {
        const actualExtraction = extractionData?.extraction

        // Same reasoning as handleSave above: don't let onDelete silently close the drawer as if
        // the item had been removed from the extraction document when it hasn't been.
        if (isNavigatorOrSuperuser && !actualExtraction) {
          throw new Error(EXTRACTION_UNAVAILABLE_MESSAGE)
        }

        if (isNavigatorOrSuperuser && actualExtraction) {
          const targetKey = EXTRACTION_SECTION_KEYS[sectionKey] || sectionKey
          const sectionItems = actualExtraction[targetKey] || []
          const updatedSectionItems = sectionItems.filter(candidate => !isSameExtractionItem(candidate, localItem))
          const nextExtraction = {
            ...actualExtraction,
            [targetKey]: updatedSectionItems
          }

          const snakeExtraction = model.toSnakeCaseKeys(nextExtraction)
          const putPayload = { extraction: snakeExtraction }
          await extractionPatcher(documentId, putPayload)
          setExtractionData({
            ...extractionData,
            extraction: nextExtraction
          })
        }
        await onDelete(localItem.id)
      } catch (error) {
        handleError(error)
      } finally {
        setDeleting(false)
      }
    }
  }

  const handleCancelDelete = event => setShowDeleteConfirm(false)

  const toggleJson = event => setJsonOpen(open => !open)

  const sectionLabel = SECTION_ITEM_LABELS[sectionKey] || 'Item'
  const lowercasedSectionLabel = sectionLabel.toLowerCase()

  const excludeFields =
    !isNavigatorOrSuperuser && !isIppPlan && sectionKey === SECTION_KEY_GOALS ? ['secondaryGoals'] : undefined

  // Tracks whether the drawer was already open on the previous run of the effect below, so a
  // closed→open transition ("reopened") can be told apart from the effect merely re-running while
  // the same item stays open (e.g. because `extraction` changed). That distinction matters because
  // MUI's exit transition — which is what normally clears localItem back to null once fully closed
  // (see TransitionProps.onExited below) — can be abandoned mid-animation if the drawer reopens for
  // the same item before it finishes, leaving localItem's stale data (e.g. isNew from before a save)
  // in place. Forcing a fresh reclone from `item` on every genuine reopen, bypassing the
  // "already showing this item" shortcut just below, avoids depending on that animation completing.
  const wasOpenRef = useRef(false)

  useEffect(() => {
    const reopened = open && !wasOpenRef.current
    wasOpenRef.current = open

    if (item && open) {
      if (!reopened && localItem && isSameExtractionItem(localItem, item)) {
        return
      }
      let itemToUse = cloneDeep(item)
      if (isNavigatorOrSuperuser && extraction) {
        const targetKey = EXTRACTION_SECTION_KEYS[sectionKey] || sectionKey
        const extractionItem = extraction[targetKey]?.find(candidate => isSameExtractionItem(candidate, item))

        if (extractionItem) {
          itemToUse = cloneDeep(extractionItem)
        }
      }
      itemToUse = fillDefaultKeys(itemToUse, sectionKey, planType)
      // IPP service/support items legitimately keep serviceType as a plain string.
      if (!isIppPlan && itemToUse.serviceType != null && !Array.isArray(itemToUse.serviceType)) {
        itemToUse.serviceType = itemToUse.serviceType === '' ? [] : [itemToUse.serviceType]
      }
      setLocalItem(itemToUse)
      setJsonOpen(false)
      setExpandedIds(new Set())
    }
  }, [item, open, sectionKey, planType, isIppPlan, isNavigatorOrSuperuser, extraction, setExpandedIds, localItem])

  const classes = useStyles({ ...props, jsonOpen })

  if (!localItem) {
    return null
  }

  const secondaryGoals = localItem.secondaryGoals || []
  const isNew = Boolean(localItem.isNew)

  return (
    <Drawer
      anchor="right"
      classes={{ paper: classes.paper }}
      ModalProps={{ BackdropProps: { invisible: true } }}
      onClose={onClose}
      open={open}
      TransitionProps={{ onExited: () => setLocalItem(null) }}
    >
      <header className={classes.header}>
        <BrandIconButton className={classes.closeIconButton} onClick={onClose} aria-label="Close drawer">
          <CloseIcon className={classes.closeIcon} />
        </BrandIconButton>

        <Typography className={classes.title}>{isNew ? `Add ${sectionLabel}` : 'What needs to be fixed?'}</Typography>
      </header>

      <Box className={classes.formContainer} component="form" onSubmit={handleSave}>
        {isNew && (
          <Typography className={classes.requiredFieldsHint}>{requiredFieldsMessage(sectionKey, planType)}</Typography>
        )}

        <ExtractionFieldEditor
          fieldKey={sectionKey}
          value={localItem}
          path={[]}
          trail={[sectionLabel]}
          depth={0}
          sectionKey={sectionKey}
          planType={planType}
          disabledKeys={
            [SECTION_KEY_ACCOMMODATIONS, SECTION_KEY_MODIFICATIONS].includes(sectionKey) ? ['type'] : undefined
          }
          excludeKeys={excludeFields}
          onChange={handleFieldChange}
          onAddItem={handleAddItem}
          onRemoveItem={handleRemoveItem}
          isExpanded={isExpanded}
          onToggleExpanded={toggleExpanded}
          inline
        />

        {sectionKey === SECTION_KEY_GOALS && !isIppPlan && !isNavigatorOrSuperuser && (
          <Box className={classes.nestedSection}>
            <Box className={classes.nestedHeader}>
              <Typography className={classes.nestedTitle} variant="body2">
                Short term objectives
              </Typography>
            </Box>

            {secondaryGoals.map((objective, index) => {
              const targetDate = objective.targetDate || ''
              const goalText = objective.goalText || ''

              return (
                <Box key={index} className={classes.nestedItem}>
                  <IconButton
                    className={classes.nestedDeleteButton}
                    size="small"
                    onClick={handleRemoveObjective(index)}
                    aria-label="Remove objective"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>

                  <TextField
                    className={classes.field}
                    fullWidth
                    variant="filled"
                    label="Target Date"
                    value={targetDate}
                    onChange={handleObjectiveDateChange(index)}
                    placeholder="YYYY-MM"
                  />

                  <TextField
                    fullWidth
                    multiline
                    variant="filled"
                    minRows={2}
                    label="Objective Text"
                    value={goalText}
                    onChange={handleObjectiveTextChange(index)}
                  />
                </Box>
              )
            })}

            <Button
              className={classes.addObjectiveButton}
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddObjective}
            >
              add a short term objective
            </Button>
          </Box>
        )}

        <Divider className={classes.divider} />

        <Box className={classes.jsonBlock}>
          <Typography className={classes.jsonTitle} onClick={toggleJson} variant="subtitle2">
            JSON
            <IconButton size="small">
              <AddIcon className={classes.jsonAddIcon} />
            </IconButton>
          </Typography>

          <Collapse in={jsonOpen} unmountOnExit>
            {jsonOpen && <pre className={classes.jsonPre}>{JSON.stringify(localItem, null, 2)}</pre>}
          </Collapse>
        </Box>

        {!isNew && (
          <Box className={classes.dangerZone}>
            <Button
              className={classes.deleteButton}
              variant="outlined"
              color="inherit"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={saving || deleting}
            >
              Delete {lowercasedSectionLabel}
            </Button>
          </Box>
        )}
      </Box>

      <footer className={classes.footer}>
        <BrandButton className={classes.cancelButton} disabled={saving || deleting} variant="text" onClick={onClose}>
          Cancel
        </BrandButton>

        <Box className={classes.footerActions}>
          {documentId && documentFileViewPath && (
            <BrandButton
              className={classes.viewIepButton}
              component={Link}
              target="_blank"
              rel="noopener noreferrer"
              to={documentFileViewPath(documentId)}
              variant="outlined"
            >
              View {planLabel} (new tab)
            </BrandButton>
          )}

          <BrandButton
            className={classes.saveButton}
            disabled={saving || deleting || !isRequiredFieldsSatisfied(localItem, sectionKey, planType)}
            variant="contained"
            onClick={handleSave}
            startIcon={saving || deleting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {deleting ? 'Deleting...' : saving ? 'Saving...' : isNew ? `Add ${sectionLabel}` : 'Save changes'}
          </BrandButton>
        </Box>
      </footer>

      <ConfirmationDialog
        open={showDeleteConfirm}
        title={`Are you sure you want to delete this ${lowercasedSectionLabel}?`}
        description={`This ${lowercasedSectionLabel} was pulled from your ${planLabel}. You should only delete it if it's incorrect or doesn't apply. Deleting it here won't change your official ${planLabel}, but it will remove it from your workspace and recommendations. This cannot be undone`}
        cancelLabel="No, back to editing"
        confirmLabel="Yes, delete"
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmButtonComponent={BrandButton}
        confirmButtonProps={{
          variant: 'outlined',
          className: classes.confirmDialogDeleteButton
        }}
        cancelButtonProps={{
          classes: { root: classes.confirmDialogCancelButton }
        }}
        classes={{ paper: classes.confirmDialogPaper }}
      />
      <ErrorSnackbar error={error} setError={setError} />
    </Drawer>
  )
}

IepSectionItemEditDrawer.propTypes = {
  open: PropTypes.bool.isRequired, // If true, the drawer is open.
  item: PropTypes.object, // The active item object currently being edited.
  sectionKey: PropTypes.oneOf([
    SECTION_KEY_GOALS,
    SECTION_KEY_SERVICES,
    SECTION_KEY_ACCOMMODATIONS,
    SECTION_KEY_MODIFICATIONS,
    SECTION_KEY_PLOPS,
    SECTION_KEY_PRIORITIES,
    SECTION_KEY_OUTCOMES,
    SECTION_KEY_SUPPORTS
  ]), // Selected category context key matching whitelisted extraction fields.
  documentId: PropTypes.string, // UUID of the document the item belongs to.
  planType: PropTypes.string, // The item's plan type, selecting copy and section vocabularies.
  extractionPatcher: PropTypes.func, // Function that PATCHes the extraction document. (defaults to the IEP endpoint)
  onClose: PropTypes.func.isRequired, // Callback triggered when closing the drawer.
  onSave: PropTypes.func.isRequired, // Callback to handle validating and saving the edited item.
  onDelete: PropTypes.func.isRequired, // Callback to handle removing the item.
  withRecommendationsOnly: PropTypes.bool // Flag to indicate if the user is in the "Only recommendation" filtered view.
}

export default IepSectionItemEditDrawer
